import { useState, useTransition } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "~/components/Button";
import { Input } from "~/components/input";
import type { CustomerSearchResult } from "~/types";

import { CustomerPicker } from "./CustomerPicker";

export function RegisterClusterForm({ onSuccess }: { onSuccess?: () => void }) {
  const navigate = useNavigate();
  const [isPending, startTransition] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] =
    useState<CustomerSearchResult | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!selectedCustomer) return;

    // @ts-expect-error async callback passed to startTransition
    startTransition(async () => {
      const formData = new FormData(e.currentTarget);
      const clusterId = formData.get("cluster_id");
      const trimmedClusterId =
        typeof clusterId === "string" ? clusterId.trim() : clusterId;
      const apiKey = formData.get("api_key");
      const trimmedApiKey = typeof apiKey === "string" ? apiKey.trim() : "";

      try {
        const response = await fetch(`/api/clusters/manual-register`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            customer_id: selectedCustomer.id,
            cluster_id: trimmedClusterId,
            // Omitted when blank: the server reuses the customer's latest key.
            ...(trimmedApiKey ? { api_key: trimmedApiKey } : {}),
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (onSuccess) {
            onSuccess();
          }
          navigate("/", {
            state: {
              flash: data.message || "Cluster registered",
            },
          });
        } else {
          const errorData = await response.json().catch(() => ({}));
          setError(errorData.error || "Failed to register cluster");
        }
      } catch (error) {
        console.error("Error:", error);
        setError("An error occurred while processing your request");
      }
    });
  };

  return (
    <form method="post" className="space-y-4" onSubmit={handleSubmit}>
      {error ? <p className="text-red-500">{error}</p> : null}
      <div>
        <label htmlFor="customer_id" className="mb-1 block text-sm">
          Customer
        </label>
        <CustomerPicker
          id="customer_id"
          disabled={isPending}
          value={selectedCustomer}
          onChange={setSelectedCustomer}
        />
        <p className="mt-1 text-xs text-gray-500">
          Don&apos;t see the customer?{" "}
          <a
            href="https://turbopuffer.com/dashboard/admin/customers"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline hover:text-blue-800"
          >
            Create one in the dashboard
          </a>
          .
        </p>
      </div>
      <Input
        type="text"
        name="cluster_id"
        className="w-full"
        disabled={isPending}
        placeholder="Enter cluster ID (e.g. aws-us-east-1)"
        required
      />
      <div>
        <Input
          type="text"
          name="api_key"
          className="w-full"
          disabled={isPending}
          placeholder="Cluster's API key (optional)"
        />
        <p className="mt-1 text-xs text-gray-500">
          Leave blank to reuse the customer&apos;s most recent API key (e.g.
          from their BYOC kit). A new key is generated only if they have none.
        </p>
      </div>
      <div className="flex justify-end gap-2">
        <Button
          className="rounded bg-gray-100 px-4 py-2 text-sm hover:bg-gray-200"
          type="submit"
          disabled={isPending || !selectedCustomer}
        >
          {isPending ? "Registering..." : "Register Cluster"}
        </Button>
      </div>
    </form>
  );
}
